import type { PuppyId } from '../game/types';

// Per-puppy alignment offsets tuned visually in AnimDebugScene. Values are
// in screen pixels at the debug scene's reference puppy display size
// (ALIGNMENT_REFERENCE_SIZE). Callers using a different display size should
// use the helpers below, which scale the offset proportionally.

export const ALIGNMENT_REFERENCE_SIZE = 450;

export interface AlignmentOffset { dx: number; dy: number }

// Offset applied when showing the still frame (pNa1/frame1) per puppy.
export const STILL_OFFSET: Partial<Record<PuppyId, AlignmentOffset>> = {
  doug: { dx: 5, dy: 10 },
  zoe: { dx: 8, dy: 28 },
  wesley: { dx: 36, dy: 0 },
  chief: { dx: 27, dy: 25 },
  wally: { dx: 5, dy: 42 },
  princess: { dx: 16, dy: 45 },
  fabio: { dx: 0, dy: -18 },
  clancy: { dx: 0, dy: 13 },
  bella: { dx: 5, dy: 10 },
  alex: { dx: -3, dy: 21 },
};

// Offset applied throughout a given animation per (puppy, animIdx).
// animIdx is 0-based, matching AnimDebugScene's index.
export const ANIM_OFFSET: Partial<Record<PuppyId, AlignmentOffset[]>> = {
  doug: [
    { dx: 4, dy: 11 }, // anim 1
    { dx: 4, dy: 9 },  // anim 2
    { dx: 5, dy: 7 },  // anim 3
    { dx: 5, dy: 11 }, // anim 4
    { dx: 3, dy: 7 },  // anim 5
  ],
  zoe: [
    { dx: 8, dy: 28 },  // anim 1
    { dx: 8, dy: 28 },  // anim 2
    { dx: 8, dy: 28 },  // anim 3
    { dx: 7, dy: 26 },  // anim 4
    { dx: 55, dy: 78 }, // anim 5
  ],
  wesley: [
    { dx: 35, dy: 0 },  // anim 1
    { dx: 35, dy: 0 },  // anim 2
    { dx: 35, dy: 0 },  // anim 3
    { dx: 35, dy: 0 },  // anim 4
    { dx: 84, dy: 45 }, // anim 5
  ],
  chief: [
    { dx: 24, dy: 21 }, // anim 1
    { dx: 24, dy: 21 }, // anim 2
    { dx: 24, dy: 21 }, // anim 3
    { dx: 24, dy: 21 }, // anim 4
    { dx: 79, dy: 78 }, // anim 5
  ],
  wally: [
    { dx: 4, dy: 42 },  // anim 1
    { dx: 4, dy: 42 },  // anim 2
    { dx: 4, dy: 42 },  // anim 3
    { dx: 4, dy: 42 },  // anim 4
    { dx: 52, dy: 93 }, // anim 5
  ],
  princess: [
    { dx: 13, dy: 45 }, // anim 1
    { dx: 4, dy: 2 },   // anim 2
    { dx: 13, dy: 45 }, // anim 3
    { dx: 4, dy: 2 },   // anim 4
    { dx: 4, dy: 2 },   // anim 5
  ],
  fabio: [
    { dx: 0, dy: -18 },  // anim 1
    { dx: 80, dy: -10 }, // anim 2
    { dx: 0, dy: -18 },  // anim 3
    { dx: 0, dy: -18 },  // anim 4
    { dx: 0, dy: -18 },  // anim 5
  ],
  clancy: [
    { dx: 0, dy: 13 },   // anim 1
    { dx: 0, dy: 13 },   // anim 2
    { dx: 0, dy: 13 },   // anim 3
    { dx: 36, dy: 24 },  // anim 4
    { dx: 0, dy: 13 },   // anim 5
  ],
  bella: [
    { dx: 5, dy: 10 },   // anim 1
    { dx: 5, dy: 10 },   // anim 2
    { dx: 5, dy: 10 },   // anim 3
    { dx: 5, dy: 10 },   // anim 4
    // Bella has only 4 anims (numAnims=4 in Puppy.ts roster).
  ],
  alex: [
    { dx: -7, dy: 21 },  // anim 1
    { dx: -7, dy: 21 },  // anim 2
    { dx: -7, dy: 21 },  // anim 3
    { dx: -7, dy: 21 },  // anim 4
    { dx: 37, dy: 72 },  // anim 5
  ],
};

export function alignmentForStill(puppyId: PuppyId, displaySize: number): AlignmentOffset {
  const base = STILL_OFFSET[puppyId];
  if (!base) return { dx: 0, dy: 0 };
  const s = displaySize / ALIGNMENT_REFERENCE_SIZE;
  return { dx: base.dx * s, dy: base.dy * s };
}

export function alignmentForAnim(
  puppyId: PuppyId,
  animIdx: number,
  displaySize: number,
): AlignmentOffset {
  const base = ANIM_OFFSET[puppyId]?.[animIdx];
  if (!base) return { dx: 0, dy: 0 };
  const s = displaySize / ALIGNMENT_REFERENCE_SIZE;
  return { dx: base.dx * s, dy: base.dy * s };
}
